import React from "react";
import { motion } from "framer-motion";
import {
  Circle,
  Code,
  Javascript,
  Html5,
  Css3,
  Terminal,
  Goal,
  Dog,
  CodeXml,
} from "lucide-react"; // optional - you can replace with react-icons if you prefer

// Dummy image from your upload (local path)
const DUMMY_IMG = "https://i.postimg.cc/NjR1gXDD/young_tender_curly_girl_holding_documents_removebg_preview.png";

/**
 * Floating icon config:
 *  - angle: degrees where the icon starts on the orbit-cm16
 *  - radius: px offset from center (overridden by CSS size)
 *  - speed: seconds for full rotation (different speeds)
 *  - direction: 1 clockwise, -1 anticlockwise
 *  - label: optional small text (we're using icon components)
 */
const floatIcons = [
  { id: "js", Icon: Circle, angle: -40, speed: 9, direction: 1, label: "JS" },
  { id: "html", Icon: Goal, angle: 20, speed: 13, direction: -1, label: "HTML" },
  { id: "css", Icon: Dog, angle: 120, speed: 11, direction: 1, label: "CSS" },
  { id: "php", Icon: CodeXml, angle: 200, speed: 15, direction: -1, label: "DEV" },
  { id: "term", Icon: Terminal, angle: 260, speed: 8, direction: 1, label: "API" },
];

export default function HeroCard16() {
  return (
    <>
      <section className="why-section">
        <div className="content">
          <h4 className="eyebrow">Why Choose Us</h4>
          <h2 className="title">We Believe That Every Problem Has A Solution</h2>
          <p className="desc">
            Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem
            Ipsum has been the industry's standard dummy text ever since.
          </p>

          <ul className="bullets">
            <li>100% Better Results</li>
            <li>Valuable Ideas</li>
            <li>Budget Friendly Theme</li>
            <li>Happy Customers</li>
          </ul>
        </div>

        <div className="visual">
          {/* Outer offset colored ring + main circular image */}
          <div className="circle-wrap">
            <div className="outer-ring" aria-hidden />
            <div className="inner-image">
              <img src={DUMMY_IMG} alt="dummy" />
            </div>

            {/* floating tech icons (orbit-cm16ing) */}
            <div className="orbit-cm16">
              {floatIcons.map((cfg, i) => {
                const { Icon, angle, speed, direction, id, label } = cfg;
                // Using CSS transform for initial placement, framer-motion to rotate around center
                return (
                  <motion.div
                    key={id}
                    className="orbit-cm16-item"
                    // style={{ ["--angle" as any]: `${angle}deg` }}
                    animate={{ rotate: direction === 1 ? 360 : -360 }}
                    transition={{
                      repeat: Infinity,
                      ease: "linear",
                      duration: speed,
                      delay: i * 0.15,
                    }}
                    aria-hidden
                    title={label}
                  >
                    <div className="orbit-cm16-icon">
                      <Icon size={18} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <style>{`
/* ---------- Layout ---------- */
.why-section {
  display: flex;
  gap: 40px;
  align-items: center;
  justify-content: space-between;
  padding: 80px 6%;
  background: #fff;
  color: #111;
  flex-wrap: wrap;
}

/* Left content */
.content {
  flex: 1 1 520px;
  max-width: 640px;
}
.eyebrow {
  color: #2b7acb;
  font-weight: 700;
  margin: 0 0 10px 0;
  font-size: 1rem;
}
.title {
  font-size: 2.2rem;
  line-height: 1.05;
  margin: 0 0 16px 0;
  font-weight: 800;
}
.desc {
  color: #444;
  margin-bottom: 22px;
  max-width: 640px;
}
.bullets {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 28px;
  list-style: none;
  padding: 0;
  margin: 0;
  color: #111;
}
.bullets li {
  padding-left: 18px;
  position: relative;
  font-weight: 600;
}
.bullets li::before {
  content: "✔";
  position: absolute;
  left: 0;
  top: 0;
  color: #2b7acb;
  font-size: 0.9rem;
  line-height: 1;
}

/* ---------- Visual (right) ---------- */
.visual {
  flex: 0 0 420px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* circle wrap defines the relative coordinate system */
.circle-wrap {
  width: 420px;
  height: 420px;
  position: relative;
  display: grid;
  place-items: center;
  pointer-events: none; /* icons should not block mouse unless you want them clickable */
}

/* Outer colored offset ring behind the image */
.outer-ring {
  position: absolute;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2b7acb 0%, #1f6fb0 100%);
  transform: translate(30px, 12px); /* offset to the bottom-right like screenshot */
  z-index: 0;
  box-shadow: 0 8px 26px rgba(43,122,203,0.14);
}

/* The circular image (on top) */
.inner-image {
  width: 300px;
  height: 300px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  z-index: 2;
  display: grid;
  place-items: center;
  box-shadow: 0 8px 40px rgba(6,12,30,0.18);
  border: 8px solid rgba(255,255,255,0.6); /* thin white border to visually separate */
  background: #eee;
}
.inner-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform-origin: center;
  transform: scale(1.03); /* slight crop for better framing */
}

/* ---------- Orbiting icons ---------- */
.orbit-cm16 {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
}

/* Each orbit-cm16-item is a full-size positioned element that rotates around center.
   CSS variable --angle defines the start position on the circle. */
.orbit-cm16-item {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  transform-origin: center;
  /* Place at given angle and move outward by translateY to radius */
  /* the child .orbit-cm16-icon is then rotated back so icon stays upright */
  transform: rotate(var(--angle)) translateY(-170px) rotate(calc(-1 * var(--angle)));
}

/* Icon bubble */
.orbit-cm16-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255,255,255,0.96);
  display: grid;
  place-items: center;
  box-shadow: 0 6px 18px rgba(10,18,40,0.12);
  font-weight: 700;
  color: #1f6fb0;
  border: 3px solid rgba(43,122,203,0.06);
  transform: translateY(-28px); /* small lift so it visually sits on orbit-cm16 */
}

/* Slight scale / float on hover for clarity (only on non-pointer devices pointer-events disabled) */
.orbit-cm16-icon:hover { transform: translateY(-28px) scale(1.06); pointer-events: auto; }

/* ---------- Responsive ---------- */
@media (max-width: 980px) {
  .why-section { padding: 48px 5%; gap: 28px; }
  .visual { flex-basis: 360px; }
  .circle-wrap { width: 360px; height: 360px; }
  .outer-ring { width: 320px; height: 320px; transform: translate(24px, 10px); }
  .inner-image { width: 260px; height: 260px; border-width: 6px; }
  .orbit-cm16-item { transform: rotate(var(--angle)) translateY(-145px) rotate(calc(-1 * var(--angle))); }
  .orbit-cm16-icon { width: 52px; height: 52px; }
}

@media (max-width: 680px) {
  .why-section {
    flex-direction: column;
    align-items: center;
    padding: 36px 4%;
  }
  .content { text-align: center; max-width: 680px; }
  .bullets { grid-template-columns: 1fr; gap: 12px 12px; justify-items: center; }
  .visual { margin-top: 22px; }
}
      `}</style>
    </>
  );
}
